import React, { useMemo, useState } from 'react';
import { getDossierUrl } from '@/core/utils/getDossierUrl';
import { useTranslation } from 'react-i18next';
import {
  BdsAvatar,
  BdsTypo,
  BdsIcon,
  BdsTooltip,
  BdsButton,
} from '@/core/constants/webComponents';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { isStaticInteraction } from '@/core/utils/isStaticInteraction';
import { REFRESH_SUPPORTED_PLATFORMS } from '@/core/constants/platforms';
import { formatStat } from '../types';

interface LeftPanelProps {
  post: Post;
  avatarThumbnail: string;
  channelData: any;
  platform: string;
  metricsUpdatedAt: string;
  likes: number | null;
  shares: number | null;
  comments: number | null;
  followers: number | null;
  onDispatch: (eventName: string, extra?: Record<string, unknown>) => void;
  CLASSIFICATION_EVENTS: any;
}

export const LeftPanel: React.FC<LeftPanelProps> = ({
  post,
  avatarThumbnail,
  channelData,
  platform,
  metricsUpdatedAt,
  likes,
  shares,
  comments,
  followers,
  onDispatch,
  CLASSIFICATION_EVENTS,
}) => {
  const { t } = useTranslation();
  const [isBanning, setIsBanning] = useState(false);

  const dossierURL = useMemo(() => getDossierUrl(post.userData), [post.userData]);

  const handleDossierClick = () => {
    if (dossierURL) {
      window.open(dossierURL, '_blank', 'noopener,noreferrer');
    }
  };

   // Listen for ban result to reset loading state
  React.useEffect(() => {
    const handleSuccess = () => setIsBanning(false);
    const handleIdError = () => setIsBanning(false);

    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_SUCCESS, handleSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_ID_ERROR, handleIdError);

    return () => {
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_SUCCESS, handleSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_ID_ERROR, handleIdError);
    };
  }, [CLASSIFICATION_EVENTS]);

  const handleBanConfirm = () => {
    setIsBanning(true);
    onDispatch(CLASSIFICATION_EVENTS.BAN_USER);
  };

  return (
    <aside className="classification-card__left">
      <div className="classification-card__user-box">
        <BdsTooltip position="right-center" tooltip-text={t('classificationCard.tooltipViewProfile', { name: post.name })}>
          <button
            type="button"
            className={`classification-card__avatar-name-wrap${dossierURL ? ' classification-card__avatar-name-wrap--clickable' : ''}`}
            onClick={dossierURL ? handleDossierClick : undefined}
            disabled={!dossierURL}
          >
            <BdsAvatar
              class="classification-card__avatar"
              size="standard"
              name={post.name}
              thumbnail={avatarThumbnail}
            />
            <BdsTypo
              class="classification-card__user-name"
              tag="span"
              variant="fs-16"
              bold="bold"
              margin={false}
            >
              {post.name}
            </BdsTypo>
          </button>
        </BdsTooltip>

        <div className="classification-card__platform-row">
          {channelData?.icons?.default ? (
            <img
              src={channelData.icons.default}
              alt={t('classificationCard.platformIconAlt')}
              width={24}
              height={24}
            />
          ) : (
            <BdsIcon theme="outline" type="logo" name={platform} size="medium" />
          )}
          <BdsTypo
            class="classification-card__last-activity"
            tag="span"
            variant="fs-10"
            italic
            margin={false}
          >
            {post.posted_at}
          </BdsTypo>
        </div>
      </div>

      <div className="classification-card__stats">
        <div className="classification-card__stats-row">
          <BdsTooltip position="right-center" tooltip-text={t('classificationCard.tooltipLikes', { count: likes ?? 0 })}>
            <span className="classification-card__stat-tag">
              <BdsIcon name="heart" size="x-small" />
              <span className="classification-card__stat-value">{formatStat(likes)}</span>
            </span>
          </BdsTooltip>
          <BdsTooltip position="right-center" tooltip-text={t('classificationCard.tooltipShares', { count: shares ?? 0 })}>
            <span className="classification-card__stat-tag">
              <BdsIcon name="share" size="x-small" />
              <span className="classification-card__stat-value">{formatStat(shares)}</span>
            </span>
          </BdsTooltip>
        </div>
        <div className="classification-card__stats-row">
          <BdsTooltip position="right-center" tooltip-text={t('classificationCard.tooltipComments', { count: comments ?? 0 })}>
            <span className="classification-card__stat-tag">
              <BdsIcon name="message-ballon" size="x-small" />
              <span className="classification-card__stat-value">{formatStat(comments)}</span>
            </span>
          </BdsTooltip>
          <BdsTooltip position="right-center" tooltip-text={t('classificationCard.tooltipFollowers', { count: followers ?? 0 })}>
            <span className="classification-card__stat-tag">
              <BdsIcon name="team" size="x-small" />
              <span className="classification-card__stat-value">
                {formatStat(followers)}
              </span>
            </span>
          </BdsTooltip>
        </div>
        <BdsTypo
          class="classification-card__data-updated"
          tag="span"
          variant="fs-10"
          italic
          margin={false}
        >
          {t('classificationCard.dataUpdatedAt')} {metricsUpdatedAt}
        </BdsTypo>
      </div>

      <div className="classification-card__user-actions">
        {REFRESH_SUPPORTED_PLATFORMS.has(platform) && !isStaticInteraction(post) && (
          <BdsButton variant="text" color="primary" icon="refresh" onClick={() => onDispatch(CLASSIFICATION_EVENTS.REFRESH_DATA)}>
            {t('classificationCard.updateData')}
          </BdsButton>
        )}
        <hr className="classification-card__divider classification-card__divider--user" />
        <BdsButton variant="text" color="negative" icon="false" onClick={handleBanConfirm} disabled={isBanning} data-testid="ban-user-btn">
          {t('classificationCard.banUser')}
        </BdsButton>
      </div>
    </aside>
  );
};
